import {
  getCountries,
  getCountryCallingCode,
  isValidPhoneNumber,
  parsePhoneNumberFromString,
  type CountryCode,
} from 'libphonenumber-js/min'

export type PhoneCountryCode = CountryCode

export type PhoneCountryOption = {
  code: PhoneCountryCode
  callingCode: string
  label: string
}

export type NormalizePhoneResult =
  | {
      ok: true
      e164: string | null
      country: PhoneCountryCode | null
      international: string | null
      national: string | null
    }
  | {
      ok: false
      error: string
    }

function regionDisplayName(code: string): string {
  try {
    const displayNames = new Intl.DisplayNames(['en'], { type: 'region' })
    return displayNames.of(code) || code
  } catch {
    return code
  }
}

export const PHONE_COUNTRY_OPTIONS: PhoneCountryOption[] = getCountries()
  .map((code) => ({
    code,
    callingCode: `+${getCountryCallingCode(code)}`,
    label: `${regionDisplayName(code)} (${`+${getCountryCallingCode(code)}`})`,
  }))
  .sort((a, b) => a.label.localeCompare(b.label))

export function guessPhoneCountryFromLocale(locale?: string | null): PhoneCountryCode | '' {
  if (!locale) return ''
  const parts = locale.replace('_', '-').split('-')
  const region = parts.at(-1)?.toUpperCase()
  if (!region) return ''
  return PHONE_COUNTRY_OPTIONS.some((option) => option.code === region) ? (region as PhoneCountryCode) : ''
}

export function normalizePhoneNumber(
  value: string | null | undefined,
  defaultCountry?: PhoneCountryCode | '' | null,
): NormalizePhoneResult {
  const raw = value?.trim() || ''
  if (!raw) {
    return { ok: true, e164: null, country: null, international: null, national: null }
  }

  if (!raw.startsWith('+') && !defaultCountry) {
    return {
      ok: false,
      error: 'Select a country or enter the number with the full +country code.',
    }
  }

  const parsed = parsePhoneNumberFromString(raw, defaultCountry || undefined)
  if (!parsed || !parsed.isValid()) {
    return {
      ok: false,
      error: 'Enter a valid phone number for the selected country.',
    }
  }

  return {
    ok: true,
    e164: parsed.number,
    country: parsed.country || defaultCountry || null,
    international: parsed.formatInternational(),
    national: parsed.formatNational(),
  }
}

export function parseStoredPhoneNumber(value: string | null | undefined) {
  const raw = value?.trim() || ''
  if (!raw) return null
  const parsed = parsePhoneNumberFromString(raw)
  if (!parsed || !parsed.isValid()) return null
  return {
    e164: parsed.number,
    country: parsed.country || null,
    international: parsed.formatInternational(),
    national: parsed.formatNational(),
  }
}

export function isValidStoredPhoneNumber(value: string | null | undefined): boolean {
  const raw = value?.trim() || ''
  if (!raw) return false
  return isValidPhoneNumber(raw)
}
